import { z } from 'zod';

import { ContentStatus, ExploreBlockType, Gender } from '@addmile/backend-client';

import { LANGUAGE_VALUES } from '../../const';

const base = {
  slug: z.string().min(1, 'Slug is required'),
  title: z.string().min(1, 'Title is required').max(200, 'Title too long'),
  type: z.enum(ExploreBlockType),
  imageUrl: z.string().url('Invalid URL'),
  showFeedback: z.boolean(),
  status: z.enum(ContentStatus),
  personalizations: z.array(z.string()).min(1, 'At least one personalization required'),
  language: z.enum(LANGUAGE_VALUES),
  collections: z.array(z.string()).optional(),
  collectionPositions: z.record(z.string(), z.number().int().min(1)).optional(),
  details: z.string().optional(),
};

const contentField = {
  content: z.string().min(1, 'Content is required'),
};
const authorField = {
  author: z.object({
    name: z.string(),
    title: z.string(),
    photoUrl: z.string().url('Invalid photo URL'),
    gender: z.enum(Gender),
  }),
};
const videoField = { videoUrl: z.string().url('Invalid URL') };
const introField = { intro: z.string().min(1, 'Intro is required') };

export const blockFormSchema = z.discriminatedUnion('type', [
  z.object({ ...base, type: z.literal(ExploreBlockType.Action), ...contentField }),
  z.object({ ...base, type: z.literal(ExploreBlockType.Video), ...videoField }),
  z.object({
    ...base,
    type: z.literal(ExploreBlockType.Article),
    ...contentField,
    ...authorField,
    generateAudio: z.boolean(),
  }),
  z.object({
    ...base,
    type: z.literal(ExploreBlockType.Practice),
    ...introField,
    ...contentField,
    ...authorField,
  }),
  z.object({ ...base, type: z.literal(ExploreBlockType.Reflect), ...introField, ...contentField }),
  z.object({
    ...base,
    type: z.literal(ExploreBlockType.Quiz),
    ...introField,
    ...contentField,
    ...authorField,
    details: z.string().min(1, 'Details are required'),
  }),
  z.object({
    ...base,
    type: z.literal(ExploreBlockType.MythBuster),
    ...introField,
    ...contentField,
  }),
  z.object({
    ...base,
    type: z.literal(ExploreBlockType.MissingPiece),
    ...introField,
    ...contentField,
  }),
  z.object({
    ...base,
    type: z.literal(ExploreBlockType.MatchUp),
    ...introField,
    ...contentField,
  }),
  z.object({
    ...base,
    type: z.literal(ExploreBlockType.RightSteps),
    ...introField,
    ...contentField,
  }),
  z.object({
    ...base,
    type: z.literal(ExploreBlockType.YourCall),
    ...introField,
    ...contentField,
    details: z.string().min(1, 'Details are required'),
  }),
]);

export type BlockFormData = z.infer<typeof blockFormSchema>;
